/* ol-auto-add-consult.js
   ───────────────────────
   Optional practitioner consult manager (sitewide).

   The $49 practitioner consult (variant 48803001663732, product
   gid://shopify/Product/9535810896116, handle practitioner-consult-session) is
   an OPTIONAL add-on — NOT forced by the cart.

   The PDP module (snippet ol-consult-optin) is an explicit add/remove control:
     • "Add to cart" adds ONE consult. There is only ever ONE consult line in the
       cart, no matter how many practitioner products are added.
     • Once added, the button becomes "Remove" and removes the consult.
     • State stays in sync across every practitioner product page and the cart.

   History: previously a forced coupling (2026-07-03 → opt-in tickbox → this
   explicit add/remove control). The practitioner screening questionnaire
   (ol-practitioner-gate) is separately parked in ol-product-hero.

   NOTE: cart-icon.js sets the header count straight from the cart:update event
   (it does NOT re-fetch), so every cart:update we dispatch must carry the real
   item_count with a non-'product-form-component' source (absolute set). */
(function () {
  var config = document.currentScript;
  var CONSULT_VARIANT_ID = Number(config && config.dataset.consultVariantId);
  var available = !!CONSULT_VARIANT_ID && config.dataset.consultAvailable === 'true';
  var cartRoot = (config && config.dataset.cartRoot) || '/';
  var busy = false;

  function request(path, options) {
    return fetch(cartRoot + path, options).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) throw new Error(data.description || data.message || 'Unable to update your cart. Please try again.');
        return data;
      });
    });
  }

  function showError(message) {
    document.querySelectorAll('[data-consult-toggle]').forEach(function (box) {
      var error = box.querySelector('[data-consult-error]');
      if (!error) {
        error = document.createElement('p');
        error.setAttribute('data-consult-error', '');
        error.setAttribute('role', 'alert');
        error.style.gridColumn = '1 / -1';
        box.appendChild(error);
      }
      error.textContent = message;
      error.hidden = !message;
    });
  }

  function fetchCart() {
    return request('cart.js', { headers: { Accept: 'application/json' } });
  }

  function consultLineOf(cart) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].variant_id === CONSULT_VARIANT_ID) return items[i];
    }
    return null;
  }

  /* Reflect cart state onto every consult module currently on the page. */
  function paintToggles(inCart) {
    var modules = document.querySelectorAll('[data-consult-toggle]');
    for (var i = 0; i < modules.length; i++) {
      var box = modules[i];
      var btn = box.querySelector('[data-consult-btn]');
      box.classList.toggle('ol-consult--added', inCart);
      if (btn) {
        btn.textContent = inCart ? 'Remove' : (available ? 'Add to cart' : 'Currently unavailable');
        btn.setAttribute('data-state', inCart ? 'remove' : 'add');
        btn.disabled = busy || (!inCart && !available);
      }
    }
  }

  /* Tell the theme to refresh the cart UI. cart-icon reads itemCount off this
     event; cart-items re-fetches its section for drawer/cart-page contents. */
  function broadcast(itemCount) {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: { data: { itemCount: itemCount, source: 'ol-consult' } }
    }));
  }

  var syncing = false;

  /* Reconcile the module UI with the cart AND enforce the single-consult
     invariant. Only re-broadcasts if it had to clamp a stray quantity, so the
     cart:update we listen for can't drive an infinite loop. */
  function sync() {
    if (syncing) return Promise.resolve();
    syncing = true;
    return fetchCart().then(function (cart) {
      var line = consultLineOf(cart);

      if (line && line.quantity > 1) {
        /* Max one consult, ever — clamp any stray quantity back to 1. */
        return request('cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 1 })
        }).then(function (updated) {
          paintToggles(true);
          syncing = false;
          broadcast(updated.item_count);
        });
      }

      paintToggles(!!line);
      syncing = false;
    }).catch(function () { syncing = false; });
  }

  /* Add the single consult (guarded against duplicates). */
  function addConsult() {
    return fetchCart().then(function (cart) {
      if (consultLineOf(cart)) return null; /* already present */
      if (!available) throw new Error('The consultation is currently unavailable. Please contact us.');
      return request('cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: CONSULT_VARIANT_ID, quantity: 1 })
      });
    }).then(function () {
      return fetchCart().then(function (cart) { broadcast(cart.item_count); });
    });
  }

  /* Remove the consult line if present. */
  function removeConsult() {
    return fetchCart().then(function (cart) {
      var line = consultLineOf(cart);
      if (!line) return null; /* nothing to remove */
      return request('cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: 0 })
      });
    }).then(function () {
      return fetchCart().then(function (cart) { broadcast(cart.item_count); });
    });
  }

  /* Add / remove button. The action self-corrects against the real cart, so a
     stale data-state can't add a second consult or fail to remove. */
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-consult-btn]') : null;
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled || busy) return;
    busy = true;
    showError('');
    var removing = btn.getAttribute('data-state') === 'remove';
    btn.disabled = true;
    btn.textContent = removing ? 'Removing…' : 'Adding…';
    (removing ? removeConsult() : addConsult())
      .catch(function (error) { showError(error.message || 'Unable to update your cart. Please try again.'); })
      .then(function () {
        busy = false;
        btn.disabled = false;
        btn.textContent = removing ? 'Remove' : 'Add to cart';
        return sync();
      });
  });

  /* Any cart change (our add/remove, a cart-page removal, a native add, …) re-syncs. */
  document.addEventListener('cart:update', sync);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
})();
